/**
 * PhotostudioProjectsPage Component
 *
 * Lists all user's Photostudio projects.
 * Features:
 * - Project grid with thumbnails
 * - Create new project
 * - Rename/delete projects
 */

import { useReducer } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FolderPlus, Folder, MoreVertical, Pencil, Trash2, Image, Loader2 } from 'lucide-react';
import { usePhotostudioProjects } from '@/hooks/usePhotostudioProjects';
import type { IPhotostudioProject } from '@/@types/photostudio';
import type { Id } from '../../../convex/_generated/dataModel';

interface IProjectCardProps {
  project: IPhotostudioProject;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function ProjectCard({ project, onClick, onRename, onDelete }: IProjectCardProps) {
  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:ring-2 hover:ring-primary"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted">
        {project.thumbnailUrl ? (
          <img src={project.thumbnailUrl} alt={project.name} className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center">
            <Folder className="size-12 text-muted-foreground/50" />
          </div>
        )}

        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-2 top-2 size-8 opacity-0 transition-opacity group-hover:opacity-100"
              onClick={e => e.stopPropagation()}
            >
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={e => {
                e.stopPropagation();
                onRename();
              }}
            >
              <Pencil className="mr-2 size-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="mr-2 size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="truncate font-medium">{project.name}</p>
        <p className="flex items-center gap-1 text-sm text-muted-foreground">
          <Image className="size-3.5" />
          {project.imageCount} image{project.imageCount !== 1 ? 's' : ''}
        </p>
      </div>
    </Card>
  );
}

interface IPhotostudioProjectsPageProps {
  onProjectSelect: (projectId: Id<'photostudioProjects'>) => void;
}

interface IProjectsPageState {
  dialog: 'none' | 'create' | 'rename' | 'delete';
  selectedProject: IPhotostudioProject | null;
  newName: string;
  isSubmitting: boolean;
}

type ProjectsPageAction =
  | { type: 'OPEN_CREATE' }
  | { type: 'OPEN_RENAME'; project: IPhotostudioProject }
  | { type: 'OPEN_DELETE'; project: IPhotostudioProject }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'SET_NAME'; name: string }
  | { type: 'SET_SUBMITTING'; value: boolean };

function projectsReducer(
  state: IProjectsPageState,
  action: ProjectsPageAction
): IProjectsPageState {
  switch (action.type) {
    case 'OPEN_CREATE':
      return { ...state, dialog: 'create', newName: '', selectedProject: null };
    case 'OPEN_RENAME':
      return {
        ...state,
        dialog: 'rename',
        selectedProject: action.project,
        newName: action.project.name,
      };
    case 'OPEN_DELETE':
      return { ...state, dialog: 'delete', selectedProject: action.project };
    case 'CLOSE_DIALOG':
      return { ...state, dialog: 'none', selectedProject: null, newName: '', isSubmitting: false };
    case 'SET_NAME':
      return { ...state, newName: action.name };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value };
  }
}

const INITIAL_PROJECTS_STATE: IProjectsPageState = {
  dialog: 'none',
  selectedProject: null,
  newName: '',
  isSubmitting: false,
};

export function PhotostudioProjectsPage({ onProjectSelect }: IPhotostudioProjectsPageProps) {
  const { projects, isLoading, error, createProject, updateProject, deleteProject } =
    usePhotostudioProjects();

  const [state, dispatch] = useReducer(projectsReducer, INITIAL_PROJECTS_STATE);

  const handleCreateProject = async () => {
    if (!state.newName.trim()) return;

    dispatch({ type: 'SET_SUBMITTING', value: true });
    const result = await createProject(state.newName.trim());
    dispatch({ type: 'SET_SUBMITTING', value: false });

    if (result.success) {
      dispatch({ type: 'CLOSE_DIALOG' });
      if (result.projectId) {
        onProjectSelect(result.projectId);
      }
    }
  };

  const handleRenameProject = async () => {
    if (!state.selectedProject || !state.newName.trim()) return;

    dispatch({ type: 'SET_SUBMITTING', value: true });
    const result = await updateProject(state.selectedProject._id, state.newName.trim());
    dispatch({ type: 'SET_SUBMITTING', value: false });

    if (result.success) {
      dispatch({ type: 'CLOSE_DIALOG' });
    }
  };

  const handleDeleteProject = async () => {
    if (!state.selectedProject) return;

    dispatch({ type: 'SET_SUBMITTING', value: true });
    const result = await deleteProject(state.selectedProject._id);
    dispatch({ type: 'SET_SUBMITTING', value: false });

    if (result.success) {
      dispatch({ type: 'CLOSE_DIALOG' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Projects</h2>
          <p className="text-sm text-muted-foreground">Organize your generated images</p>
        </div>
        <Button onClick={() => dispatch({ type: 'OPEN_CREATE' })}>
          <FolderPlus className="mr-2 size-4" />
          New Project
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Folder className="mb-4 size-16 text-muted-foreground/50" />
          <p className="text-lg font-medium">No projects yet</p>
          <p className="mb-4 text-sm text-muted-foreground">
            Create a project to organize your generated images
          </p>
          <Button onClick={() => dispatch({ type: 'OPEN_CREATE' })}>
            <FolderPlus className="mr-2 size-4" />
            Create First Project
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {projects.map(project => (
            <ProjectCard
              key={project._id}
              project={project}
              onClick={() => onProjectSelect(project._id)}
              onRename={() => dispatch({ type: 'OPEN_RENAME', project })}
              onDelete={() => dispatch({ type: 'OPEN_DELETE', project })}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog
        open={state.dialog === 'create'}
        onOpenChange={open => !open && dispatch({ type: 'CLOSE_DIALOG' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Project Name</Label>
              <Input
                id="create-name"
                value={state.newName}
                onChange={e => dispatch({ type: 'SET_NAME', name: e.target.value })}
                placeholder="My Project"
                maxLength={100}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => dispatch({ type: 'CLOSE_DIALOG' })}
              disabled={state.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!state.newName.trim() || state.isSubmitting}
            >
              {state.isSubmitting ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <FolderPlus className="mr-2 size-4" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={state.dialog === 'rename'}
        onOpenChange={open => !open && dispatch({ type: 'CLOSE_DIALOG' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rename-name">Project Name</Label>
              <Input
                id="rename-name"
                value={state.newName}
                onChange={e => dispatch({ type: 'SET_NAME', name: e.target.value })}
                maxLength={100}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => dispatch({ type: 'CLOSE_DIALOG' })}
              disabled={state.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameProject}
              disabled={!state.newName.trim() || state.isSubmitting}
            >
              {state.isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={state.dialog === 'delete'}
        onOpenChange={open => !open && dispatch({ type: 'CLOSE_DIALOG' })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{state.selectedProject?.name}"? This will also delete
              all {state.selectedProject?.imageCount || 0} images in this project. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={state.isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={state.isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {state.isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
